<script lang="ts">
  import { getOverlayStore, closeOverlay } from '$stores/overlay.js';

  interface Props {
    open?: boolean;
    position?: 'center' | 'bottom';
    openAction?: () => void;
    close?: () => void;
    afterOpen?: () => void;
    afterClose?: () => void;
    /**
     * When true, this Overlay ignores the shared overlay store and is driven
     * only by its `open` prop. Use for demos or one-off overlays that should
     * not respond to Modal triggers. Default: false (store-subscribed).
     */
    standalone?: boolean;
    /**
     * Store registry ID. Production apps leave this as `'default'`. Storybook
     * passes a per-story ID so state doesn't leak between stories.
     */
    storeId?: string;
  }

  let {
    open = $bindable(false),
    position = $bindable('center'),
    openAction,
    close,
    afterOpen,
    afterClose,
    standalone = false,
    storeId = 'default',
  }: Props = $props();


  let visible = $state(false);
  let animatingOpen = $state(false);
  let backdropEl: HTMLDivElement | undefined = $state();

  // Shared-store mirroring: the root-mounted <a2u-ui-overlay> subscribes to
  // its registry entry. Multiple Modals using the same storeId all drive the
  // same atoms.
  let storeOpenValue = $state(false);
  let storePosValue = $state<'center' | 'bottom'>('center');
  let syncing = false;

  $effect(() => {
    if (standalone) return;
    const { overlayOpen, overlayPosition } = getOverlayStore(storeId);
    const unsub1 = overlayOpen.subscribe((v) => { storeOpenValue = v; });
    const unsub2 = overlayPosition.subscribe((v) => { storePosValue = v; });
    return () => {
      unsub1();
      unsub2();
    };
  });

  $effect(() => {
    if (standalone) return;
    if (storeOpenValue !== open) {
      syncing = true;
      open = storeOpenValue;
      position = storePosValue;
      queueMicrotask(() => { syncing = false; });
    } else if (storePosValue !== position) {
      position = storePosValue;
    }
  });

  $effect(() => {
    if (open) {
      visible = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          animatingOpen = true;
        });
      });

      openAction?.();
      $host()?.dispatchEvent(new CustomEvent('open', { bubbles: true, composed: true }));

      setTimeout(() => {
        if (typeof afterOpen === 'function') afterOpen();
        $host()?.dispatchEvent(new CustomEvent('afterOpen', { bubbles: true, composed: true }));
      }, 350);
    } else {
      if (visible) {
        animatingOpen = false;

        close?.();
        $host()?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));

        setTimeout(() => {
          visible = false;
          afterClose?.();
          $host()?.dispatchEvent(new CustomEvent('afterClose', { bubbles: true, composed: true }));
        }, 300);
      }
    }
  });

  function dismiss() {
    close?.();
    $host()?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
    if (!standalone && !syncing) closeOverlay(storeId);
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      dismiss();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss();
    }
  }

  export function show() { open = true; }
  export function hide() { open = false; }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="backdrop"
  class:visible
  class:open={animatingOpen}
  class:bottom={position === 'bottom'}
  bind:this={backdropEl}
  onclick={handleBackdropClick}
  onkeydown={handleKeydown}
>
  <slot></slot>
</div>

<style lang="scss">
  @use '../../../../tokens/breakpoints' as *;

  :host {
    display: contents;
  }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--a2u-ui-core-spacing-16);
    gap: var(--a2u-ui-core-spacing-8);
    background: var(--a2u-ui-color-bg-overlay-1); /* TODO: confirm with design — overlay tier choice */
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity 300ms ease-in, visibility 300ms ease-in;

    &.visible {
      visibility: visible;
      pointer-events: auto;
    }

    &.open {
      opacity: 1;
      transition: opacity 350ms ease-out, visibility 350ms ease-out;
    }
  }

  .backdrop.bottom {
    align-items: stretch;
    justify-content: flex-end;
    padding: 0;
  }

  @media (max-width: $breakpoint-sm-max) {
    .backdrop {
      align-items: stretch;
      justify-content: flex-end;
      padding: 0;
    }
  }
</style>
